/**
 * Window-key collection for the refresh-cost estimate — the rule-tree walk,
 * extracted from estimate-refresh-cost.ts (which sits at the file cap).
 *
 * Group windows mirror the backend's collectExcludeDateRanges; leaf compareTo
 * windows mirror collectCompareToDateRanges, including its action-chain
 * gating — a leaf counts when an ENCLOSING group matches the rotation filter.
 * The compareTo key itself comes from the SHARED resolveCompareWindow, so that
 * half cannot drift from the backend by construction.
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  resolveCompareWindow,
  ruleCompareWindow,
  type RuleAction,
  type RuleNode,
} from '@trafficpulse/shared';

/** Rule actions the QUEUE path judges on; anything else costs it nothing. */
export const ROTATION_ACTIONS: readonly RuleAction[] = [
  'set-to-queue',
  'set-to-waiting',
  'eliminate',
];

/** A rule group's OWN window, or null when it inherits one. */
function ruleKey(node: Extract<RuleNode, { kind: 'group' }>): string | null {
  const dr = node.dateRange;
  if (!dr) return null;
  if (dr !== 'custom') return dr;
  const { dateRangeCustomFrom: from, dateRangeCustomTo: to } = node;
  return from && to ? `custom:${from}:${to}` : null;
}

function leafCompareKey(node: Extract<RuleNode, { kind: 'condition' }>): string | null {
  if (!node.compareTo) return null;
  const win = resolveCompareWindow(ruleCompareWindow(node.compareTo));
  return win.kind === 'key' ? win.cubeKey : null;
}

const matchesRotation = (node: Extract<RuleNode, { kind: 'group' }>): boolean =>
  Boolean(node.actions?.some((a) => ROTATION_ACTIONS.includes(a)));

/** Walk a rule tree collecting group-window AND leaf-compareTo keys. */
export function collectRuleWindowKeys(
  tree: RuleNode,
  rotationOnly: boolean,
  into: Set<string>,
): void {
  const visit = (node: RuleNode, matched: boolean): void => {
    if (node.kind === 'not') return visit(node.child, matched);
    if (node.kind === 'group') {
      const groupCounts = !rotationOnly || matchesRotation(node);
      const key = groupCounts ? ruleKey(node) : null;
      if (key) into.add(key);
      const nextMatched = matched || groupCounts;
      for (const child of node.children ?? []) visit(child, nextMatched);
      return;
    }
    if (node.kind !== 'condition' || !matched) return;
    const key = leafCompareKey(node);
    if (key) into.add(key);
  };
  visit(tree, !rotationOnly);
}
